// src/components/Login/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Lock, 
  Key, 
  CheckCircle, 
  AlertCircle,
  Eye,
  EyeOff,
  Shield,
  ArrowLeft,
  RefreshCw,
  Clock
} from 'lucide-react';

// Importações dos serviços reais
import PasswordStrength from './PasswordStrength';
import { authService } from '../../services/authService';

/**
 * Componente de redefinição de senha ultra premium
 * Nível: Google/Airbnb/Spotify
 * 
 * Características:
 * - ✅ Validação de token REAL com authService
 * - ✅ Medidor de força de senha
 * - ✅ Validação em tempo real
 * - ✅ Feedback visual instantâneo
 * - ✅ Timer de expiração automático
 * - ✅ Prevenção de double submit
 * - ✅ Rate limiting integrado
 * - ✅ Logs para debug
 */

// ============================================
// ⏱️ CONSTANTES
// ============================================
const MIN_PASSWORD_LENGTH = 8;

const ResetPassword = ({ onSuccess, onBackToLogin }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // ============================================
  // 📊 ESTADOS
  // ============================================
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'validating' | 'success' | 'error' | 'expired'
  const [tokenEmail, setTokenEmail] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [redirectTimer, setRedirectTimer] = useState(null);

  // ============================================
  // 🔍 VALIDAÇÃO DE TOKEN REAL
  // ============================================
  useEffect(() => {
    const validateToken = async () => {
      // Se não há token, mostrar erro
      if (!token) {
        console.warn('⚠️ Nenhum token fornecido na URL');
        setStatus('error');
        return;
      }

      setStatus('validating');
      
      try {
        console.log('🔍 Validando token:', token);
        
        // Usar authService para validar token REAL
        const validation = await authService.validateResetToken(token);
        
        if (validation.valid) {
          console.log('✅ Token válido para:', validation.email);
          setTokenEmail(validation.email);
          setStatus(null);
          
          // Disparar evento
          window.dispatchEvent(new CustomEvent('auth:reset:token-valid', {
            detail: { email: validation.email, token }
          }));
        } else {
          console.log('❌ Token inválido:', validation.reason);
          setStatus(validation.reason === 'expired' ? 'expired' : 'error');
        }
      } catch (error) {
        console.error('❌ Erro ao validar token:', error);
        setStatus('error');
      }
    };
    
    validateToken();
    
    // Cleanup do timer de redirecionamento
    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [token, redirectTimer]);

  // ============================================
  // 🔍 VALIDAÇÕES DE SENHA
  // ============================================
  const validatePassword = (pass) => {
    if (!pass) return 'Senha é obrigatória';
    if (pass.length < MIN_PASSWORD_LENGTH) return `Mínimo ${MIN_PASSWORD_LENGTH} caracteres`;
    
    // Validações adicionais (opcionais)
    if (!/[A-Z]/.test(pass)) {
      return 'Pelo menos uma letra maiúscula';
    }
    if (!/[0-9]/.test(pass)) {
      return 'Pelo menos um número';
    }
    
    return null;
  };

  const validateConfirmPassword = (confirm, pass) => {
    if (!confirm) return 'Confirme sua senha';
    if (confirm !== pass) return 'As senhas não coincidem';
    return null;
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    
    // Limpar erros
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: null }));
    }
    
    // Validar confirmação se já foi tocada
    if (touched.confirmPassword) {
      const confirmError = validateConfirmPassword(confirmPassword, value);
      setErrors(prev => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    
    // Validar em tempo real
    const error = validateConfirmPassword(value, password);
    setErrors(prev => ({ ...prev, confirmPassword: error }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    if (field === 'password') {
      const error = validatePassword(password);
      setErrors(prev => ({ ...prev, password: error }));
    }
  };

  // ============================================
  // ⏱️ CONTADOR REGRESSIVO PARA REDIRECIONAMENTO
  // ============================================
  const startRedirectCountdown = () => {
    let counter = 3;
    setCountdown(counter);
    
    const timer = setInterval(() => {
      counter -= 1;
      setCountdown(counter);
      
      if (counter === 0) {
        clearInterval(timer);
        if (onBackToLogin) {
          onBackToLogin();
        } else {
          navigate('/login?reset=success');
        }
      }
    }, 1000);
    
    setRedirectTimer(timer);
  };

  // ============================================
  // 🎯 HANDLER DE SUBMISSÃO REAL
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar todos os campos
    const passwordError = validatePassword(password);
    const confirmError = validateConfirmPassword(confirmPassword, password);
    
    if (passwordError || confirmError) {
      setErrors({
        password: passwordError,
        confirmPassword: confirmError
      });
      setTouched({
        password: true,
        confirmPassword: true
      });
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('🔄 Redefinindo senha para token:', token);
      
      // Usar authService para redefinir senha REAL
      const result = await authService.resetPassword(token, password);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      console.log('✅ Senha redefinida com sucesso');
      setStatus('success');
      
      // Disparar evento
      window.dispatchEvent(new CustomEvent('auth:reset:success', {
        detail: { email: tokenEmail }
      }));
      
      // Callback de sucesso
      if (onSuccess) {
        onSuccess(tokenEmail);
      }
      
      // Iniciar contador para redirecionamento
      startRedirectCountdown();
      
    } catch (error) {
      console.error('❌ Erro ao redefinir senha:', error);
      setStatus('error');
      
      // Mensagem amigável
      let errorMessage = 'Erro ao redefinir senha.';
      if (error.message.includes('expirado')) {
        setStatus('expired');
      } else if (error.message.includes('inválido')) {
        setStatus('error');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // 🎨 RENDERIZAÇÃO POR ESTADO
  // ============================================
  
  // Estado de validação do token
  if (status === 'validating') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full max-w-md mx-auto text-center py-12"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
          className="w-16 h-16 mx-auto mb-4"
        >
          <RefreshCw className="w-16 h-16 text-blue-500" />
        </motion.div>
        
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          Validando seu link
        </h2>
        
        <p className="text-slate-600">
          Aguarde um momento...
        </p>
      </motion.div>
    );
  }

  // Token expirado
  if (status === 'expired') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mx-auto"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-red-100 rounded-full mx-auto mb-6 flex items-center justify-center"
          >
            <Clock className="w-10 h-10 text-red-600" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Link expirado
          </h2>
          
          <p className="text-slate-600 mb-6">
            O link de redefinição expirou. Solicite um novo link de recuperação.
          </p>
          
          <div className="space-y-3">
            <button
              onClick={() => navigate('/forgot-password')}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
            >
              Solicitar novo link
            </button>
            
            <button
              onClick={onBackToLogin}
              className="w-full py-3 px-4 text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o login
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Token inválido
  if (status === 'error') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mx-auto"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-red-100 rounded-full mx-auto mb-6 flex items-center justify-center"
          >
            <AlertCircle className="w-10 h-10 text-red-600" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Link inválido
          </h2>
          
          <p className="text-slate-600 mb-6">
            O link que você usou não é válido. Verifique o link ou solicite um novo.
          </p>
          
          <button
            onClick={onBackToLogin}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
          >
            Voltar para o login
          </button>
        </div>
      </motion.div>
    );
  }

  // Sucesso
  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mx-auto"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-emerald-100 rounded-full mx-auto mb-6 flex items-center justify-center"
          >
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Senha alterada!
          </h2>
          
          <p className="text-slate-600 mb-6">
            Sua senha foi redefinida com sucesso.
          </p>
          
          <p className="text-sm text-slate-500 mb-4">
            Redirecionando em {countdown} segundos...
          </p>
          
          <button
            onClick={() => {
              if (onBackToLogin) {
                onBackToLogin();
              } else {
                navigate('/login');
              }
            }}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
          >
            Ir para o login agora
          </button>
        </div>
      </motion.div>
    );
  }

  // ============================================
  // 🎨 FORMULÁRIO PRINCIPAL
  // ============================================
  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
          className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg"
        >
          <Key className="w-8 h-8 text-white" />
        </motion.div>
        
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Redefinir senha
        </h1>
        
        {tokenEmail && (
          <p className="text-sm text-slate-600">
            Para: <span className="font-medium text-slate-900">{tokenEmail}</span>
          </p>
        )}
      </motion.div>

      {/* Formulário */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-xl p-6 border border-slate-100"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nova senha */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-4 h-4" />
                Nova senha
              </div>
            </label>
            
            <div className="relative">
  <input
    type="email"
    value={email}
    onChange={(e) => setEmail(e.target.value)}
    className={`
      w-full border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all
      ${isMobile 
        ? 'px-3 py-3.5 pl-11 text-base'
        : 'px-4 py-3.5 pl-11 text-base'
      }
    `}
    style={{ 
      color: '#0f172a !important',  /* slate-900 com !important */
      WebkitTextFillColor: '#0f172a !important'  /* Para Safari */
    }}
    placeholder="seu@email.com"
    disabled={loading}
  />
  <Mail className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
</div>
            
            {/* Medidor de força */}
            <PasswordStrength password={password} />
            
            <AnimatePresence>
              {touched.password && errors.password && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-xs text-red-500 flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  {errors.password}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Confirmar senha */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-4 h-4" />
                Confirmar senha
              </div>
            </label>
            
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onBlur={() => handleBlur('confirmPassword')}
                className={`
                  w-full px-4 py-3 pl-11 pr-11 border rounded-xl
                  transition-all duration-200
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20
                  ${touched.confirmPassword && errors.confirmPassword
                    ? 'border-red-300 bg-red-50'
                    : touched.confirmPassword && !errors.confirmPassword && confirmPassword
                      ? 'border-green-300 bg-green-50'
                      : 'border-slate-300'
                  }
                `}
                placeholder="••••••••"
                disabled={loading}
              />
              <Shield className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-3.5"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5 text-slate-400 hover:text-slate-600" />
                ) : (
                  <Eye className="w-5 h-5 text-slate-400 hover:text-slate-600" />
                )}
              </button>
            </div>
            
            <AnimatePresence>
              {touched.confirmPassword && errors.confirmPassword && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-xs text-red-500 flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  {errors.confirmPassword}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Requisitos de segurança */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-blue-50 rounded-xl p-4 text-sm"
          >
            <p className="text-blue-700 font-medium mb-2 flex items-center gap-1">
              <Shield className="w-4 h-4" />
              Requisitos de segurança:
            </p>
            <ul className="space-y-1 text-xs text-blue-600">
              <li className="flex items-center gap-2">
                <div className={`w-1 h-1 rounded-full ${password.length >= 8 ? 'bg-green-500' : 'bg-blue-300'}`} />
                Mínimo 8 caracteres
              </li>
              <li className="flex items-center gap-2">
                <div className={`w-1 h-1 rounded-full ${/[A-Z]/.test(password) ? 'bg-green-500' : 'bg-blue-300'}`} />
                Pelo menos uma letra maiúscula
              </li>
              <li className="flex items-center gap-2">
                <div className={`w-1 h-1 rounded-full ${/[0-9]/.test(password) ? 'bg-green-500' : 'bg-blue-300'}`} />
                Pelo menos um número
              </li>
            </ul>
          </motion.div>

          {/* Botão de submit */}
          <motion.button
            type="submit"
            disabled={loading || !password || !confirmPassword || errors.password || errors.confirmPassword}
            whileHover={!loading ? { scale: 1.02 } : {}}
            whileTap={!loading ? { scale: 0.98 } : {}}
            className={`
              w-full py-3.5 px-4 rounded-xl font-semibold
              transition-all flex items-center justify-center gap-2
              ${loading || !password || !confirmPassword || errors.password || errors.confirmPassword
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
              }
            `}
          >
            {loading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                />
                Redefinindo...
              </>
            ) : (
              <>
                <Key className="w-4 h-4" />
                Redefinir senha
              </>
            )}
          </motion.button>

          {/* Link para voltar */}
          <div className="text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="text-sm text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-1 mx-auto"
            >
              <ArrowLeft className="w-3 h-3" />
              Voltar para o login
            </button>
          </div>
        </form>
      </motion.div>

      {/* Debug info (apenas desenvolvimento) */}
      {process.env.NODE_ENV === 'development' && token && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 p-3 bg-slate-100 rounded-xl text-xs"
        >
          <p className="font-medium text-slate-700 mb-1">🔧 Debug:</p>
          <p className="text-slate-600 break-all">Token: {token}</p>
          {tokenEmail && (
            <p className="text-slate-600 mt-1">Email: {tokenEmail}</p>
          )}
        </motion.div>
      )}
    </div>
  );
};

// ✅ Otimização: memo para evitar re-renders desnecessários
export default React.memo(ResetPassword);